import { useAtom } from 'ximple';
import { useSelector } from 'src/hooks';
import {
    cartAtom,
    selectWaitingListItems,
    selectRegularItems,
    selectCartComposition,
} from 'src/state/cart/cartAtom';
import { route } from 'src/components/common/router/Router';
import { useLocale } from '@repo/i18n';
import { useState, type JSX } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import BasketSummary from './BasketSummary';
import useCartItemsForSummary from 'src/hooks/domain/useCartItemsForSummary';

interface MixedCartToggleProps {
    activeView: 'regular' | 'waitingList';
    onActiveViewChange: (view: 'regular' | 'waitingList') => void;
    regularItemsCount: number;
    waitingListItemsCount: number;
}

function MixedCartToggle({
    activeView,
    onActiveViewChange,
    regularItemsCount,
    waitingListItemsCount,
}: MixedCartToggleProps): JSX.Element {
    const { t } = useLocale();

    return (
        <Box
            sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'left' }}>
                <ToggleButtonGroup
                    value={activeView}
                    exclusive
                    onChange={(_, newView) => newView && onActiveViewChange(newView)}
                    aria-label="cart view"
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            py: 0.25,
                            px: 1,
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: 'inherit',
                            border: '1px solid rgba(255,255,255,0.3)',
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                color: 'primary.main',
                            },
                        },
                    }}
                >
                    <ToggleButton value="regular" aria-label="regular items">
                        {t.activities} ({regularItemsCount})
                    </ToggleButton>
                    <ToggleButton value="waitingList" aria-label="waiting list items">
                        {t.waiting_list} ({waitingListItemsCount})
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </Box>
    );
}

function useBasketButtonText(
    cartComposition: ReturnType<typeof selectCartComposition>,
    activeView: 'regular' | 'waitingList',
): string {
    const { t } = useLocale();

    if (cartComposition === 'mixed') {
        return activeView === 'waitingList' ? t.join_waiting_list : t.checkout;
    }
    return cartComposition === 'waitingListOnly' ? t.join_waiting_list : t.checkout;
}

export default function BasketPopUp(props: { onCloseModal: () => void }): JSX.Element {
    const { t } = useLocale();
    const [cartItems] = useAtom(cartAtom);
    const cartComposition = useSelector(cartItems, selectCartComposition);
    const waitingListItems = useSelector(cartItems, selectWaitingListItems);
    const regularItems = useSelector(cartItems, selectRegularItems);
    const [activeView, setActiveView] = useState<'regular' | 'waitingList'>('regular');
    const nextButtonText = useBasketButtonText(cartComposition, activeView);
    const headingText = cartComposition === 'mixed' ? undefined : t.your_cart;
    const showShareButton = cartComposition !== 'mixed';

    const handleGoToCheckoutClicked = () => {
        props.onCloseModal();
        if (
            cartComposition === 'waitingListOnly' ||
            (cartComposition === 'mixed' && activeView === 'waitingList')
        ) {
            route('/waitlist');
        } else {
            route('/checkout');
        }
    };

    const cartItemsForSummary = useCartItemsForSummary();

    const summaryCartItems =
        cartComposition === 'mixed'
            ? activeView === 'waitingList'
                ? cartItemsForSummary.waitingListItems
                : cartItemsForSummary.regularItems
            : cartItemsForSummary.itemsToShow;

    return (
        <>
            {cartComposition === 'mixed' && (
                <MixedCartToggle
                    activeView={activeView}
                    onActiveViewChange={setActiveView}
                    regularItemsCount={regularItems.length}
                    waitingListItemsCount={waitingListItems.length}
                />
            )}

            <BasketSummary
                cartItems={summaryCartItems}
                cartComposition={cartComposition}
                goToCheckout={handleGoToCheckoutClicked}
                onCloseModal={props.onCloseModal}
                activeView={activeView}
                isWaitingListMode={cartItemsForSummary.isWaitingListMode}
                nextButtonText={nextButtonText}
                headingText={headingText}
                showShareButton={showShareButton}
            />
        </>
    );
}
